import { useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import UIkit from "uikit";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import classnames from "classnames";

import { Form } from "../../../../ui";
import Tags from "../../../common/Tags";
import {
  LOCATION_TYPE_OPTIONS,
  USAGE_OPTIONS,
  labelFor,
} from "../data";

const schema = yup.object().shape({
  name: yup.string().required("Location name is required"),
  code: yup.string().required("Location code is required"),
  location_type: yup.string().required("Type is required"),
});

const defaultValues = {
  name: "",
  code: "",
  location_type: "warehouse",
  address: "",
  storage_area: "",
  usage: [],
  tags: [],
  count_required: true,
  notes: "",
  active: true,
};

const StorageLocationAdd = ({
  location,
  flagIsNew,
  flagIsProcessing,
  onSave,
  onCancel,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
    reset,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const locationType = watch("location_type");
  const usage = watch("usage") || [];
  const tags = watch("tags") || [];
  const active = watch("active");
  const countRequired = watch("count_required");

  const resetForm = useCallback(() => {
    Object.keys(defaultValues).forEach((field) => {
      setValue(field, defaultValues[field]);
    });
  }, [setValue]);

  useEffect(() => {
    clearErrors();
    const source = location || {};

    Object.keys(defaultValues).forEach((field) => {
      setValue(field, source[field] !== undefined ? source[field] : defaultValues[field]);
    });

    if (!location) resetForm();
  }, [location, clearErrors, setValue, resetForm]);

  const toggleUsage = useCallback(
    (value) => {
      const current = getValues("usage") || [];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      setValue("usage", next, { shouldDirty: true });
    },
    [getValues, setValue],
  );

  const handleRegister = useCallback(
    async (formdata) => {
      const payload = {
        ...formdata,
        id: location?.id,
        count_required: !!formdata.count_required,
        active: !!formdata.active,
      };

      try {
        const result = await (onSave ? onSave(payload) : true);
        if (result !== false) {
          UIkit.offcanvas("#modal-storage-location-add").hide();
          reset(defaultValues);
        }
      } catch (err) {
        UIkit.notification("Failed to save storage location.", "error");
      }
    },
    [location, onSave, reset],
  );

  const handleInvalid = useCallback(() => {
    UIkit.notification("Complete the required location fields.", "warning");
  }, []);

  const handleClose = useCallback(() => {
    if (onCancel) onCancel();
    UIkit.offcanvas("#modal-storage-location-add").hide();
  }, [onCancel]);

  return (
    <div
      id="modal-storage-location-add"
      className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            uk-close="true"
            onClick={handleClose}
          ></button>

          <div className="uk-modal-header">
            <h2 className="uk-modal-title">
              {flagIsNew ? "Add Storage Location" : "Edit Storage Location"}
            </h2>
          </div>

          <div className="uk-modal-body">
            <div className="add-contact-form">
              <Form onSubmit={handleSubmit(handleRegister, handleInvalid)} data-uk-grid="true">
                <div className="uk-width-1-1">
                  <label className="uk-form-label">Location Name*</label>
                  <input
                    className={classnames("uk-input", { "uk-form-danger": errors.name })}
                    type="text"
                    {...register("name")}
                  />
                  {errors.name && <div className="uk-text-danger uk-text-small">{errors.name.message}</div>}
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Location Code*</label>
                  <input
                    className={classnames("uk-input", { "uk-form-danger": errors.code })}
                    type="text"
                    {...register("code")}
                  />
                  {errors.code && <div className="uk-text-danger uk-text-small">{errors.code.message}</div>}
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Type*</label>
                  <div uk-form-custom="target: > * > span:first-child">
                    <select {...register("location_type")}>
                      {LOCATION_TYPE_OPTIONS.filter((option) => option.value).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button
                      className={classnames("uk-button uk-button-default", {
                        "uk-form-danger": errors.location_type,
                      })}
                      type="button"
                      tabIndex="-1"
                    >
                      <span className="select-value">
                        {labelFor(LOCATION_TYPE_OPTIONS, locationType, "Select Type")}
                      </span>
                      <span uk-icon="icon: chevron-down" className="hms-align-right"></span>
                    </button>
                  </div>
                  {errors.location_type && (
                    <div className="uk-text-danger uk-text-small">{errors.location_type.message}</div>
                  )}
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Address / Access Notes</label>
                  <textarea className="uk-textarea" rows="2" {...register("address")} />
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Room / Shelf / Bin</label>
                  <input className="uk-input" type="text" {...register("storage_area")} />
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Allowed Uses</label>
                  <div className="uk-grid-small" data-uk-grid="true">
                    {USAGE_OPTIONS.map((option) => (
                      <label key={option.value} className="uk-width-1-2@s uk-width-1-1">
                        <input
                          className="uk-checkbox uk-margin-small-right"
                          type="checkbox"
                          checked={usage.includes(option.value)}
                          onChange={() => toggleUsage(option.value)}
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="uk-width-1-1 hms-add-tag">
                  <label className="uk-form-label">Tags</label>
                  <Tags value={tags} onChange={(editedTags) => setValue("tags", editedTags)} />
                </div>

                <div className="uk-width-1-2@s uk-text-left">
                  <label className="uk-form-label">Inventory Counts</label>
                  <div className="autosend-checkbox uk-flex uk-flex-left uk-flex-middle">
                    <label className="setting-switch">
                      <input
                        type="checkbox"
                        checked={!!countRequired}
                        onChange={(event) => setValue("count_required", event.target.checked)}
                      />
                      <span className="setting-slider setting-round"></span>
                    </label>
                    <span className="uk-margin-small-left">
                      {countRequired ? "Required" : "Optional"}
                    </span>
                  </div>
                </div>

                <div className="uk-width-1-2@s uk-text-left">
                  <label className="uk-form-label">Status</label>
                  <div className="autosend-checkbox uk-flex uk-flex-left uk-flex-middle">
                    <label className="setting-switch">
                      <input
                        type="checkbox"
                        checked={!!active}
                        onChange={(event) => setValue("active", event.target.checked)}
                      />
                      <span className="setting-slider setting-round"></span>
                    </label>
                    <span className="uk-margin-small-left">{active ? "Active" : "Inactive"}</span>
                  </div>
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Notes</label>
                  <textarea className="uk-textarea" rows="2" {...register("notes")} />
                </div>

                <div className="uk-width-1-1 uk-margin-top">
                  <button className="uk-button hms-btn hms-red-btn" type="submit">
                    {flagIsProcessing ? <div data-uk-spinner="ratio: 0.5;"></div> : "Save"}
                  </button>
                  <button
                    className="uk-button hms-btn uk-offcanvas-close uk-margin-small-left"
                    onClick={handleClose}
                    type="button"
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageLocationAdd;
